import React, { useState, useEffect } from "react";

import { Alert, Icon } from "@partials";
import { AlertType, TabType, GeoCoordinates, GEO_COORDINATES_FROM_BROWSER } from "@constants";
import { getLocalization } from "@utilities";

import { LocalizationTexts } from "../types";

type GeoLocationNotSupportedProps = {
  localization: LocalizationTexts;
};

type GeoLocationInfoProps = {
  type: TabType;
  localization: LocalizationTexts;
  iconsList: any;
};

type InfoBoxProps = {
  onClick: () => void;
} & GeoLocationInfoProps;

const GeoLocationNotSupported: React.FC<GeoLocationNotSupportedProps> = ({ localization }) => {
  return (
    <div className="mb-8">
      <Alert
        type={AlertType.Error}
        text={getLocalization(localization, "Branches_GeoLocationNotSupported")}
        textAttributes={{ role: "alert", "aria-live": "assertive" }}
        className="w-full"
      />
    </div>
  );
};

const InfoBox: React.FC<InfoBoxProps> = ({ type, localization, iconsList, onClick }) => {
  const { mapMarker } = iconsList;

  return (
    <div className="mb-12" data-testid={`geolocation-info-box-${type}`}>
      <div
        className="
          flex
          flex-col
          items-center
          rounded
          bg-light
          shadow-tab
          sm:flex-row
        "
      >
        <div className="mb-4 w-[247px] sm:mb-0 sm:w-[268px]">
          <img
            src="../../../img/webp/geolocation.webp"
            className="icon h-[153px] w-[247px] sm:h-[166px] sm:w-[268px]"
            alt="geolocation"
          />
        </div>
        <div className="w-full sm:h-[166px] sm:w-2/3 sm:py-10 sm:pl-4">
          <h2 className="mx-8 mb-4 break-words text-xl font-semibold text-grey-200 group-hover:text-red-corporate group-hover:underline sm:mx-0 sm:text-2xl">
            {getLocalization(
              localization,
              type === TabType.Branches
                ? "Branches_GeoLocationTitle_Branch"
                : "Branches_GeoLocationTitle_Atm"
            )}
          </h2>
          <div className="mx-8 mb-8 flex items-start sm:mx-0 sm:mb-0">
            <Icon
              name={mapMarker.name}
              viewBox={mapMarker.viewbox}
              alt={mapMarker.alt}
              svg={mapMarker.svg}
              className="icon inline h-6 w-6 shrink-0 fill-red-corporate"
            />
            <div className="ml-2 break-words text-base">
              {getLocalization(
                localization,
                type === TabType.Branches
                  ? "Branches_GeoLocationText_Branch"
                  : "Branches_GeoLocationText_Atm"
              )}{" "}
              <button
                type="button"
                onClick={onClick}
                className="underline"
                data-testid={`geolocation-info-button-${type}`}
              >
                {getLocalization(localization, "Branches_GeoLocationTextUnderline")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GeoLocationInfo: React.FC<GeoLocationInfoProps> = ({ type, localization, iconsList }) => {
  const [coordinatesFromBrowser, setCoordinatesFromBrowser] = useState<GeoCoordinates>({
    lat: 0,
    lng: 0,
  });

  const showPositionSuccess = (position) => {
    const latLng: GeoCoordinates = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    setCoordinatesFromBrowser(latLng);
    window.localStorage.setItem(GEO_COORDINATES_FROM_BROWSER, JSON.stringify(latLng));
  };

  const requestGeoLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(showPositionSuccess);
    }
  };

  const showGeoLocationInfo = coordinatesFromBrowser.lat === 0 && coordinatesFromBrowser.lng === 0;

  useEffect(() => {
    requestGeoLocation();
  }, []);

  if (!navigator.geolocation) return <GeoLocationNotSupported localization={localization} />;
  if (!showGeoLocationInfo) return null;

  return (
    <InfoBox
      type={type}
      localization={localization}
      iconsList={iconsList}
      onClick={requestGeoLocation}
    />
  );
};

export default GeoLocationInfo;
